// Inspired by https://redux.js.org/recipes/code-splitting#defining-an-injectreducer-function
import { combineReducers, createStore } from 'redux';

import type {
  InjectedStateKey,
  AcornStore,
  BananaStore,
  AcornStoreConfig,
  BananaStoreConfig,
} from '@web-platform/shared-universal-types/src/redux/state/state-shared-types';
import type { Reducer } from 'redux';

/**
 * Creates a dynamic Redux store with dynamic reducer injection capability.
 * Uses conditional types to return AcornStore or BananaStore based on config type.
 *
 * @param {AcornStoreConfig | BananaStoreConfig} config - Store configuration object for Acorn or Banana route.
 * @returns {AcornStore | BananaStore} A Redux store instance, typed as AcornStore or BananaStore based on config.
 */
export function createDynamicStore<TConfig extends AcornStoreConfig | BananaStoreConfig>(
  config: TConfig,
): TConfig extends AcornStoreConfig ? AcornStore : BananaStore {
  const { enhancer, initialState = {}, staticReducers } = config;
  const rootReducer = combineReducers(staticReducers);
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const store = createStore(rootReducer, initialState, enhancer) as TConfig extends AcornStoreConfig
    ? AcornStore
    : BananaStore;

  store.asyncReducers = {};
  store.staticReducers = staticReducers;

  store.injectReducer = (key: InjectedStateKey, asyncReducer: Reducer) => {
    if (store.staticReducers[key]) {
      return;
    }

    if (store.asyncReducers[key]) {
      return;
    }

    store.asyncReducers[key] = asyncReducer;

    const combinedReducer = combineReducers({
      ...store.staticReducers,
      ...store.asyncReducers,
    });
    // @ts-expect-error - To be compatible with different reducer types
    store.replaceReducer(combinedReducer);
  };

  return store;
}
